import {AfterViewInit, ChangeDetectorRef, DoCheck, Injectable, OnDestroy} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {_AbstractConstructor, _Constructor} from '@angular/material/core';
import {Subject, Subscribable, Subscription} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

/**
 * Don't forget to add providers in your component
 *
 * @example
 * providers: [
 *   {
 *     provide: UpdateTriggerContainer,
 *     useExisting: forwardRef(() =>  PopItemComponent )
 *   }
 * ],
 */
export interface UpdateTriggerContainerInterface {
  updateFormTrigger: Subscribable<any>;
}
@Injectable()
export abstract class UpdateTriggerContainer implements UpdateTriggerContainerInterface {
  updateFormTrigger = new Subject<void>();
}

export interface HasUpdateTriggerListener {
  ngAfterViewInit(): void;
  ngOnDestroy(): void;
}

export type CanUpdateTriggerListener = object;

export type HasUpdateTriggerListenerCtor = _Constructor<HasUpdateTriggerListener> &
  _AbstractConstructor<HasUpdateTriggerListener> &
  (new (...args: any[]) => HasUpdateTriggerListener);

export function mixinHasUpdateTriggerListener<
  T extends _Constructor<CanUpdateTriggerListener> | _AbstractConstructor<CanUpdateTriggerListener>
>(ngUnsubscribeField = 'ngUnsubscribe$', base?: T): HasUpdateTriggerListenerCtor & T {
  return class extends base implements AfterViewInit, OnDestroy {
    protected updateTriggerSubscription: Subscription;
    protected updateTriggerListenerChangeDetector: ChangeDetectorRef;
    protected updateTriggerContainer: UpdateTriggerContainer;
    constructor(...args: any[]) {
      super(...args);
      args.forEach(a => {
        if (a instanceof ChangeDetectorRef || a?.markForCheck) {
          this.updateTriggerListenerChangeDetector = a;
        }
        if (a instanceof UpdateTriggerContainer || a?.updateFormTrigger) {
          this.updateTriggerContainer = a;
        }
      });
    }
    ngAfterViewInit(): void {
      if (super.ngAfterViewInit) {
        super.ngAfterViewInit && super.ngAfterViewInit();
      }
      if (this.updateTriggerContainer && this.updateTriggerListenerChangeDetector) {
        this.updateTriggerSubscription = this.updateTriggerContainer.updateFormTrigger
          .pipe(takeUntil(this[ngUnsubscribeField] || new Subject()))
          .subscribe(() => {
            this.updateTriggerListenerChangeDetector.markForCheck();
          });
      }
    }

    ngOnDestroy(): void {
      if (super.ngOnDestroy) {
        super.ngOnDestroy();
      }
      if (this.updateTriggerSubscription) {
        this.updateTriggerSubscription.unsubscribe();
      }
    }
  };
}
const defaultClass = class {
  constructor(...args: any[]) {}
};
// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/ban-types
type WithUpdateTriggerListenerFn = <T extends _Constructor<object> | _AbstractConstructor<object> = typeof defaultClass>(
  ngUnsubscribeField?: string,
  base?: T
) => HasUpdateTriggerListenerCtor & T;

export const WithUpdateTriggerListener: WithUpdateTriggerListenerFn = (ngUnsubscribeField = 'ngUnsubscribe$', base?) =>
  (base
    ? mixinHasUpdateTriggerListener(ngUnsubscribeField, base)
    : mixinHasUpdateTriggerListener(ngUnsubscribeField, class {})) as unknown as any;


export interface HasCheckControlUpdates {
  ngDoCheck(): void;
}

export type CanCheckControlUpdates = object;

export type HasCheckControlUpdatesCtor<T> = _Constructor<HasCheckControlUpdates> &
  _AbstractConstructor<HasCheckControlUpdates> &
  (new (...args: any[]) => HasCheckControlUpdates & T);

export type mixinHasCheckControlUpdatesFn<
  T extends _Constructor<CanCheckControlUpdates> | _AbstractConstructor<CanCheckControlUpdates>,
  R = any
> = (base: T, controlAccessor?: (self: R) => AbstractControl) => Omit<T, 'constructor'> & HasCheckControlUpdatesCtor<T>;

export function mixinHasCheckControlUpdates(base, controlAccessor) {
  return class extends base implements DoCheck {
    private checkControlUpdatesCd: ChangeDetectorRef;
    private oldTouched;
    private oldValue;
    private oldDisabled;
    private oldErrors;

    constructor(...args: any[]) {
      super(...args);
      args.forEach(a => {
        if (a instanceof ChangeDetectorRef || a?.markForCheck) {
          this.checkControlUpdatesCd = a;
        }
      });
    }
    private getCheckControlUpdatesTarget() {
      return controlAccessor(this as any);
    }
    ngDoCheck(): void {
      const control = this.getCheckControlUpdatesTarget();
      if (control?.touched !== this.oldTouched) {
        this.oldTouched = control.touched;
        return this.checkControlUpdatesCd.markForCheck();
      }
      if (control?.disabled !== this.oldDisabled) {
        this.oldDisabled = control?.disabled;
        return this.checkControlUpdatesCd.markForCheck();
      }
      if (control?.value && control.value !== this.oldValue) {
        this.oldValue = control.value;
        return this.checkControlUpdatesCd.markForCheck();
      }
      if (JSON.stringify(control?.errors) !== JSON.stringify(this.oldErrors)) {
        this.oldErrors = control.errors;
        return this.checkControlUpdatesCd.markForCheck();
      }
    }
  };
}
// eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/ban-types
type WithCheckControlUpdatesFn = <R = any, T extends _Constructor<object> | _AbstractConstructor<object> = typeof defaultClass>(
  controlAccessor?: (self: R) => AbstractControl,
  base?: T
) => ReturnType<mixinHasCheckControlUpdatesFn<T, R>>;

export const WithCheckControlUpdates: WithCheckControlUpdatesFn = (controlAccessor, base?) =>
  (base ? mixinHasCheckControlUpdates(base, controlAccessor) : mixinHasCheckControlUpdates(class {}, controlAccessor)) as unknown as any;
